/**
 * PaneContext — provides per-pane scoped data to child components.
 *
 * Each PaneProvider wraps a single visible pane (editor + preview area).
 * Children use `usePaneContext()` to access pane-specific data without
 * reading global store singletons directly.
 *
 * Phase 2 of Multi-Pane: UI components migrate from global store selectors
 * to PaneContext-based data access.
 */

import type { ContentDefinition, ForBlock, ForDefinition, ImportDocumentSnapshot, ParsedDocument, PluginDefinition, PluginOutput, UseBinding } from "@magic-md/core";
import { buildDefaultedTemplateInputs, extractTemplateParams, getBlockingTemplateParams, substituteTemplateInputRefs } from "@magic-md/core";
import { createContext, useContext, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";
import type { EditorTab, PreviewWidth, ViewMode } from "../store/editor-store";
import { collectForScopedUnits } from "../lib/for-scope-units";
import { mergeHydratedExternalConfigDefinition } from "../lib/external-config-definition";
import { getImportCachedSymbolOutput } from "../lib/import-cache-output";
import { getCachedContentInnerParse } from "../lib/content-inner-parse-cache";
import { collectImportDocuments } from "../lib/import-documents";
import { materializePluginUseDefinition } from "../lib/use-materialization";
import { useStableSemanticValue } from "../lib/use-stable-semantic-value";
import { useEditorStore } from "../store/editor-store";
import type { ContentExecutionEntry, ExecutionEntry, ForExecutionEntry, ImportExecutionEntry, UseExecutionEntry } from "../store/plugin-execution-store";
import { usePluginExecutionStore } from "../store/plugin-execution-store";

/* ── Pane State Types ── */

export interface PaneState {
    paneId: string;
    tabId: string;
    viewMode: ViewMode;
    splitPercent: number;
    cursorLine: number;
    cursorCol: number;
}

/* ── Context Value ── */

export interface PaneContextValue {
    /** Unique pane identifier */
    paneId: string;
    /** Tab ID shown in this pane */
    tabId: string;
    /** The tab data */
    tab: EditorTab | undefined;
    /** Parsed document for the tab (from parsedDocumentMap) */
    parsedDocument: ParsedDocument | null;
    /** All definitions from the parsed document */
    definitions: Map<string, PluginDefinition>;
    /** Plugin outputs kept separate from definitions to avoid DocContext churn */
    definitionOutputs: Map<string, PluginOutput>;
    /** Persisted/parser/engine outputs, excluding live execution entries. */
    documentDefinitionOutputs: Map<string, PluginOutput>;
    /** For definitions from the parsed document */
    forDefinitions: Map<string, ForDefinition>;
    /** For blocks resolved by the Engine, including materialized ::use for templates */
    engineForBlocks: Map<string, ForBlock>;
    /** Content definitions from the parsed document */
    contentDefinitions: Map<string, ContentDefinition>;
    /** Execution entries for the tab (from session cache or active executions) */
    executions: Map<string, ExecutionEntry>;
    /** For execution entries */
    forExecutions: Map<string, ForExecutionEntry>;
    /** Content execution entries */
    contentExecutions: Map<string, ContentExecutionEntry>;
    /** Import execution entries */
    importExecutions: Map<string, ImportExecutionEntry>;
    /** Renderable import document snapshots keyed by namespace */
    importDocuments: Map<string, ImportDocumentSnapshot>;
    /** Use execution entries */
    useExecutions: Map<string, UseExecutionEntry>;
    /** Parsed and scoped use bindings, including output external refs. */
    uses: UseBinding[];
    /** Engine session ID for the tab */
    sessionId: string | null;
    /** View mode for this pane */
    viewMode: ViewMode;
    /** Split percentage for this pane */
    splitPercent: number;
    /** Preview width mode for this pane */
    previewWidth: PreviewWidth;
    /** Whether this pane has keyboard focus */
    isFocused: boolean;
}

export type PaneRenderContextValue = Omit<
    PaneContextValue,
    | "definitionOutputs"
    | "executions"
    | "forExecutions"
    | "contentExecutions"
    | "importExecutions"
    | "useExecutions"
>;

export type PaneEditorContextValue = Pick<
    PaneContextValue,
    | "paneId"
    | "tabId"
    | "tab"
    | "parsedDocument"
    | "definitions"
    | "definitionOutputs"
    | "documentDefinitionOutputs"
    | "forDefinitions"
    | "contentDefinitions"
    | "executions"
    | "forExecutions"
    | "contentExecutions"
    | "importExecutions"
    | "useExecutions"
    | "uses"
    | "sessionId"
    | "viewMode"
    | "isFocused"
>;

export interface PaneChromeContextValue {
    paneId: string;
    tabId: string;
    filePath: string;
    sourceFilePath?: string;
    title: string;
    kind: EditorTab["kind"] | undefined;
    sessionId: string | null;
    splitPercent: number;
    previewWidth: PreviewWidth;
    isFocused: boolean;
}

const PaneContext = createContext<PaneContextValue | null>(null);
const PaneRenderContext = createContext<PaneRenderContextValue | null>(null);
const PaneEditorContext = createContext<PaneEditorContextValue | null>(null);
const PaneChromeContext = createContext<PaneChromeContextValue | null>(null);

const EMPTY_EXECUTIONS = new Map<string, ExecutionEntry>();
const EMPTY_FOR_EXECUTIONS = new Map<string, ForExecutionEntry>();
const EMPTY_CONTENT_EXECUTIONS = new Map<string, ContentExecutionEntry>();
const EMPTY_IMPORT_EXECUTIONS = new Map<string, ImportExecutionEntry>();
const EMPTY_USE_EXECUTIONS = new Map<string, UseExecutionEntry>();
const EMPTY_PLUGIN_DEFINITIONS = new Map<string, PluginDefinition>();
const EMPTY_CONTENT_DEFINITIONS = new Map<string, ContentDefinition>();
const EMPTY_FOR_BLOCKS = new Map<string, ForBlock>();
const EMPTY_FOR_DEFINITIONS = new Map<string, ForDefinition>();

function isImportedNamespaceUnit(
    parsedDocument: ParsedDocument | null | undefined,
    id: string,
): boolean {
    return !!parsedDocument?.imports?.some((imp) => id.startsWith(`${imp.namespace}/`));
}

function stripDefinitionOutput(definition: PluginDefinition): PluginDefinition {
    if (definition.output === undefined) return definition;
    const { output: _output, ...rest } = definition;
    return rest;
}

function putDefinitionOutput(
    outputs: Map<string, PluginOutput>,
    id: string,
    output: PluginDefinition["output"] | null | undefined,
): void {
    if (output != null) outputs.set(id, output);
}

/* ── Provider ── */

export interface PaneProviderProps {
    paneId: string;
    children: React.ReactNode;
}

/**
 * Provides pane-scoped data to child components.
 *
 * In single-pane mode (current default), there is one PaneProvider wrapping
 * the entire editor+preview area. In multi-pane mode, each pane gets its
 * own PaneProvider.
 *
 * Data flows:
 * - Tab + parsedDocument: from editor-store per-tab Maps
 * - Executions + sessionId: from plugin-execution-store active state or session cache
 * - viewMode + splitPercent: from pane state in editor-store
 */
export function PaneProvider({ paneId, children }: PaneProviderProps) {
    const {
        paneTabId,
        paneViewMode,
        paneSplitPercent,
        panePreviewWidth,
        focusedPaneId,
        activeTabId,
        globalViewMode,
        globalSplitPercent,
        globalPreviewWidth,
    } = useEditorStore(
        useShallow((s) => {
            const pane = s.panes?.find((p) => p.paneId === paneId);
            return {
                paneTabId: pane?.tabId,
                paneViewMode: pane?.viewMode,
                paneSplitPercent: pane?.splitPercent,
                panePreviewWidth: pane?.previewWidth,
                focusedPaneId: s.focusedPaneId,
                activeTabId: s.activeTabId,
                globalViewMode: s.viewMode,
                globalSplitPercent: s.splitPercent,
                globalPreviewWidth: s.previewWidth,
            };
        }),
    );

    // Derive tabId from pane state (or fall back to activeTabId for single-pane compatibility)
    const tabId = paneTabId ?? activeTabId;

    const { tab, parsedDocument } = useEditorStore(
        useShallow((s) => ({
            tab: s.tabs.find((t) => t.id === tabId),
            parsedDocument:
                s.parsedDocumentMap.get(tabId) ??
                (tabId === s.activeTabId ? s.parsedDocument : null),
        })),
    );

    // Execution state: if this is the active tab, use the live store state;
    // otherwise read the tab's session snapshot from the session-keyed cache.
    const {
        liveExecutions,
        liveForExecutions,
        liveContentExecutions,
        liveImportExecutions,
        liveUseExecutions,
        liveSessionId,
        cachedSessionId,
        liveEngineDefinitions,
        liveEngineContentDefinitions,
        sessionSnapshot,
    } = usePluginExecutionStore(
        useShallow((s) => {
            const cachedSessionId = s.tabSessionMap.get(tabId);
            const isActiveTab = tabId === activeTabId;
            if (isActiveTab) {
                return {
                    liveExecutions: s.executions,
                    liveForExecutions: s.forExecutions,
                    liveContentExecutions: s.contentExecutions,
                    liveImportExecutions: s.importExecutions,
                    liveUseExecutions: s.useExecutions,
                    liveSessionId: s.sessionId,
                    cachedSessionId,
                    liveEngineDefinitions: s.engineDefinitions,
                    liveEngineContentDefinitions: s.engineContentDefinitions,
                    sessionSnapshot: undefined,
                };
            }

            return {
                liveExecutions: EMPTY_EXECUTIONS,
                liveForExecutions: EMPTY_FOR_EXECUTIONS,
                liveContentExecutions: EMPTY_CONTENT_EXECUTIONS,
                liveImportExecutions: EMPTY_IMPORT_EXECUTIONS,
                liveUseExecutions: EMPTY_USE_EXECUTIONS,
                liveSessionId: null,
                cachedSessionId,
                liveEngineDefinitions: EMPTY_PLUGIN_DEFINITIONS,
                liveEngineContentDefinitions: EMPTY_CONTENT_DEFINITIONS,
                sessionSnapshot: cachedSessionId
                    ? s.sessionExecutionCache.get(cachedSessionId)
                    : undefined,
            };
        }),
    );
    const executions =
        tabId === activeTabId
            ? liveExecutions
            : (sessionSnapshot?.executions ?? EMPTY_EXECUTIONS);
    const forExecutions =
        tabId === activeTabId
            ? liveForExecutions
            : (sessionSnapshot?.forExecutions ?? EMPTY_FOR_EXECUTIONS);
    const contentExecutions =
        tabId === activeTabId
            ? liveContentExecutions
            : (sessionSnapshot?.contentExecutions ?? EMPTY_CONTENT_EXECUTIONS);
    const importExecutions =
        tabId === activeTabId
            ? liveImportExecutions
            : (sessionSnapshot?.importExecutions ?? EMPTY_IMPORT_EXECUTIONS);
    const useExecutions =
        tabId === activeTabId
            ? liveUseExecutions
            : (sessionSnapshot?.useExecutions ?? EMPTY_USE_EXECUTIONS);
    const engineDefinitions =
        tabId === activeTabId
            ? liveEngineDefinitions
            : (sessionSnapshot?.engineDefinitions ?? EMPTY_PLUGIN_DEFINITIONS);
    const engineContentDefinitions =
        tabId === activeTabId
            ? liveEngineContentDefinitions
            : (sessionSnapshot?.engineContentDefinitions ?? EMPTY_CONTENT_DEFINITIONS);

    // Session ID: same logic — live for active tab, cached for background
    const sessionId = tabId === activeTabId ? liveSessionId : (cachedSessionId ?? null);

    const viewMode = paneViewMode ?? globalViewMode;
    const splitPercent = paneSplitPercent ?? globalSplitPercent;
    const previewWidth = panePreviewWidth ?? globalPreviewWidth;

    const isFocused = focusedPaneId === paneId;

    const contentScopedUnits = useMemo(() => {
        const scopedDefinitions = new Map<string, { definition: PluginDefinition; ownerContentId: string }>();
        const scopedContentDefinitions = new Map<string, { contentDefinition: ContentDefinition; ownerContentId: string }>();
        const scopedUses: Array<{ use: UseBinding; ownerContentId: string }> = [];
        const scopedUseOwnerById = new Map<string, string>();
        const seenUseIds = new Set<string>();
        const rootContentDefinitions =
            parsedDocument?.contentDefinitions ?? new Map<string, ContentDefinition>();
        const queue = Array.from(rootContentDefinitions, ([id, contentDefinition]) => ({
            id,
            contentDefinition,
        }));
        const seenContentIds = new Set<string>(rootContentDefinitions.keys());

        for (let i = 0; i < queue.length; i++) {
            const { id: ownerContentId, contentDefinition } = queue[i];
            try {
                const inner = getCachedContentInnerParse(contentDefinition.body);
                for (const [definitionId, definition] of inner.definitions) {
                    if (!scopedDefinitions.has(definitionId)) {
                        scopedDefinitions.set(definitionId, { definition, ownerContentId });
                    }
                }
                for (const [contentId, innerContentDefinition] of inner.contentDefinitions) {
                    if (!rootContentDefinitions.has(contentId) && !scopedContentDefinitions.has(contentId)) {
                        scopedContentDefinitions.set(contentId, {
                            contentDefinition: innerContentDefinition,
                            ownerContentId,
                        });
                    }
                    if (!seenContentIds.has(contentId)) {
                        seenContentIds.add(contentId);
                        queue.push({ id: contentId, contentDefinition: innerContentDefinition });
                    }
                }
                for (const use of inner.uses) {
                    if (!seenUseIds.has(use.id)) {
                        seenUseIds.add(use.id);
                        scopedUseOwnerById.set(use.id, ownerContentId);
                        scopedUses.push({ use, ownerContentId });
                    }
                }
            } catch {
                // ignore parse errors in content body
            }
        }

        return {
            definitions: scopedDefinitions,
            contentDefinitions: scopedContentDefinitions,
            uses: scopedUses,
            useOwnerById: scopedUseOwnerById,
        };
    }, [parsedDocument]);

    const forScopedUnits = useMemo(
        () => collectForScopedUnits(parsedDocument?.forDefinitions),
        [parsedDocument?.forDefinitions],
    );

    const rawMaterializedUses = useMemo(
        () => [
            ...(parsedDocument?.uses ?? []),
            ...forScopedUnits.uses.map((entry) => entry.use),
            ...contentScopedUnits.uses.map((entry) => entry.use),
        ],
        [parsedDocument?.uses, forScopedUnits.uses, contentScopedUnits.uses],
    );
    const materializedUses = useStableSemanticValue(rawMaterializedUses);

    // Merge top-level definitions with for-inner definitions, materialized
    // ::use bindings, and Engine-resolved definitions so that ::render directives
    // can find all definitions — including imported ns/id symbols. Runtime
    // outputs are intentionally kept out of this map; PreviewPanel feeds them
    // through DocOutputsProvider so output changes do not churn DocContext.
    const rawDefinitions = useMemo(() => {
        const defs = new Map<string, PluginDefinition>();
        for (const [id, def] of parsedDocument?.definitions ?? new Map<string, PluginDefinition>()) {
            defs.set(id, stripDefinitionOutput(def));
        }

        // ── Restore imported plugin definitions from import cache (self-describing) ──
        // This enables immediate rendering of imported definitions on document open
        // without waiting for engine sync.
        if (parsedDocument?.imports) {
            for (const imp of parsedDocument.imports) {
                const cached = imp.output;
                if (!cached) continue;
                for (const sym of [...(cached.symbols ?? []), ...(cached.instanceSymbols ?? [])]) {
                    if (sym.kind === "node" && sym.pluginName && !defs.has(sym.qualifiedId)) {
                        const def: PluginDefinition = {
                            id: sym.qualifiedId,
                            pluginName: sym.pluginName,
                            config: sym.config ?? {},
                            template: sym.template ?? false,
                            params: sym.params,
                            inputSchema: sym.inputSchema,
                        };
                        defs.set(sym.qualifiedId, def);
                    }
                }
            }
        }

        // Engine-resolved definitions (imported symbols, materialized use bindings)
        // should replace stale import-cache entries after an import rerun, but
        // must not replace definitions parsed from the current local document.
        for (const [id, def] of engineDefinitions) {
            const localDefinition = parsedDocument?.definitions?.get(id);
            const hydratedExternal = localDefinition
                ? mergeHydratedExternalConfigDefinition(localDefinition, def)
                : null;
            if (hydratedExternal) {
                defs.set(id, hydratedExternal);
                continue;
            }
            const isLocalDefinition = !!localDefinition;
            const shouldSet =
                !defs.has(id) ||
                (!isLocalDefinition && isImportedNamespaceUnit(parsedDocument, id));
            if (shouldSet) {
                defs.set(id, stripDefinitionOutput(def));
            }
        }

        if (parsedDocument?.forDefinitions) {
            for (const forDef of parsedDocument.forDefinitions.values()) {
                for (const innerDef of forDef.innerScope.definitions.values()) {
                    if (!defs.has(innerDef.id)) {
                        defs.set(innerDef.id, innerDef);
                    }
                }
            }
        }
        for (const [id, scoped] of forScopedUnits.definitions) {
            if (!defs.has(id)) {
                defs.set(id, scoped.definition);
            }
        }
        // Merge content-inner definitions so ConfigPanel and RightSidebar can find them.
        for (const [id, scoped] of contentScopedUnits.definitions) {
            if (!defs.has(id)) {
                defs.set(id, stripDefinitionOutput(scoped.definition));
            }
        }
        // Materialize ::use bindings: clone source definition with overridden config
        if (materializedUses.length > 0) {
            for (const use of materializedUses) {
                const isDeclaredDefinition =
                    (parsedDocument?.definitions?.has(use.id) ?? false) ||
                    forScopedUnits.definitions.has(use.id) ||
                    contentScopedUnits.definitions.has(use.id);
                if (isDeclaredDefinition) continue; // real definitions win ID conflicts

                const materialized = materializePluginUseDefinition(use, defs, materializedUses);
                if (!materialized) continue;
                defs.set(use.id, stripDefinitionOutput(materialized));
            }
        }
        return defs;
    }, [parsedDocument, engineDefinitions, contentScopedUnits, forScopedUnits, materializedUses]);
    const definitions = useStableSemanticValue(rawDefinitions);

    const rawDocumentDefinitionOutputs = useMemo(() => {
        const outputs = new Map<string, PluginOutput>();
        for (const [id, def] of parsedDocument?.definitions ?? new Map<string, PluginDefinition>()) {
            putDefinitionOutput(outputs, id, def.output);
        }

        if (parsedDocument?.imports) {
            for (const imp of parsedDocument.imports) {
                const cached = imp.output;
                if (!cached) continue;
                for (const sym of [...(cached.symbols ?? []), ...(cached.instanceSymbols ?? [])]) {
                    if (sym.kind !== "node" || !sym.pluginName) continue;
                    const cachedOutput = getImportCachedSymbolOutput(imp.namespace, cached, sym);
                    putDefinitionOutput(outputs, sym.qualifiedId, cachedOutput as PluginDefinition["output"] | undefined);
                }
            }
        }

        for (const [id, def] of engineDefinitions) {
            putDefinitionOutput(outputs, id, def.output);
        }

        for (const [id, scoped] of contentScopedUnits.definitions) {
            const contentOutput = contentExecutions.get(scoped.ownerContentId)?.output;
            putDefinitionOutput(
                outputs,
                id,
                (contentOutput?.[id] as PluginDefinition["output"] | undefined) ??
                    scoped.definition.output,
            );
        }

        for (const use of materializedUses) {
            const ownerContentId = contentScopedUnits.useOwnerById.get(use.id);
            const contentOutput = ownerContentId
                ? contentExecutions.get(ownerContentId)?.output
                : undefined;
            putDefinitionOutput(
                outputs,
                use.id,
                use.output?.nodeOutput ??
                    (contentOutput?.[use.id] as PluginDefinition["output"] | undefined),
            );
        }

        return outputs;
    }, [parsedDocument, engineDefinitions, contentExecutions, contentScopedUnits, materializedUses]);
    const documentDefinitionOutputs = useStableSemanticValue(rawDocumentDefinitionOutputs);

    const rawDefinitionOutputs = useMemo(() => {
        const outputs = new Map<string, PluginOutput>(documentDefinitionOutputs);
        for (const [id, exec] of executions) {
            putDefinitionOutput(outputs, id, exec.output);
        }
        return outputs;
    }, [documentDefinitionOutputs, executions]);
    const definitionOutputs = useStableSemanticValue(rawDefinitionOutputs);

    // For definitions and content definitions from parsed document
    const rawForDefinitions = useMemo(
        () => parsedDocument?.forDefinitions ?? EMPTY_FOR_DEFINITIONS,
        [parsedDocument],
    );
    const forDefinitions = useStableSemanticValue(rawForDefinitions);
    const rawContentDefinitions = useMemo(() => {
        const cDefs = new Map<string, ContentDefinition>(
            parsedDocument?.contentDefinitions ?? new Map(),
        );

        for (const [id, scoped] of contentScopedUnits.contentDefinitions) {
            if (!cDefs.has(id)) {
                const execOutput = contentExecutions.get(id)?.output;
                const ownerOutput = contentExecutions.get(scoped.ownerContentId)?.output;
                const output =
                    execOutput ??
                    (ownerOutput?.[id] as ContentDefinition["output"] | undefined) ??
                    scoped.contentDefinition.output;
                cDefs.set(id, output
                    ? { ...scoped.contentDefinition, output }
                    : scoped.contentDefinition);
            }
        }
        for (const [id, scoped] of forScopedUnits.contentDefinitions) {
            if (!cDefs.has(id)) {
                cDefs.set(id, scoped.contentDefinition);
            }
        }

        // ── Restore imported content definitions from import cache (self-describing) ──
        if (parsedDocument?.imports) {
            for (const imp of parsedDocument.imports) {
                const cached = imp.output;
                if (!cached) continue;
                for (const sym of [...(cached.symbols ?? []), ...(cached.instanceSymbols ?? [])]) {
                    if (sym.kind === "content" && sym.body != null && !cDefs.has(sym.qualifiedId)) {
                        cDefs.set(sym.qualifiedId, {
                            id: sym.qualifiedId,
                            kind: "content",
                            template: sym.template ?? false,
                            body: sym.body,
                            params: sym.params,
                            inputSchema: sym.inputSchema,
                            range: { start: 0, end: 0 },
                        });
                    }
                }
            }
        }

        // Merge Engine-resolved content definitions (imported ns/id content symbols).
        // As with plugin definitions, fresh engine data wins over stale import cache.
        for (const [id, contentDef] of engineContentDefinitions) {
            const isLocalContent = parsedDocument?.contentDefinitions?.has(id) ?? false;
            if (
                !cDefs.has(id) ||
                (!isLocalContent && isImportedNamespaceUnit(parsedDocument, id))
            ) {
                cDefs.set(id, contentDef);
            }
        }
        for (const [id, exec] of contentExecutions) {
            if (exec.output === undefined) continue;
            const contentDef = cDefs.get(id);
            if (!contentDef || contentDef.output === exec.output) continue;
            cDefs.set(id, { ...contentDef, output: exec.output });
        }
        // Materialize ::use bindings that source content definitions.
        // Plugin-sourced uses are handled in the `definitions` memo above;
        // content-sourced uses must be added here so RenderBlock's
        // three-way dispatch (for → content → definition) can find them.
        if (materializedUses.length > 0) {
            for (const use of materializedUses) {
                const isDeclaredContent =
                    (parsedDocument?.contentDefinitions?.has(use.id) ?? false) ||
                    contentScopedUnits.contentDefinitions.has(use.id) ||
                    forScopedUnits.contentDefinitions.has(use.id);
                if (isDeclaredContent) continue; // real content definitions win ID conflicts
                if (definitions.has(use.id)) continue; // materialized as plugin def
                const contentSource = cDefs.get(use.source);
                const ownerContentId = contentScopedUnits.useOwnerById.get(use.id);
                const existingContent = cDefs.get(use.id);

                // materializedBody: prefer execution store (post-hydration) over parsed stub
                const useExec = useExecutions.get(use.id);
                const materializedBody = use.output?.materializedBody ?? useExec?.materializedBody;

                // cachedContentOutput: prefer execution store (post-hydration) over parsed stub
                const cachedContentOutput = (
                    use.output?.contentOutput ??
                    contentExecutions.get(use.id)?.output ??
                    (ownerContentId
                        ? contentExecutions.get(ownerContentId)?.output?.[use.id]
                        : undefined)
                ) as ContentDefinition["output"] | undefined;

                if (contentSource) {
                    // Normal path: source content definition is available
                    const expandedBody = materializedBody ??
                        (contentSource.template
                            ? substituteTemplateInputRefs(
                                contentSource.body,
                                buildDefaultedTemplateInputs(use.with, contentSource.inputSchema),
                            )
                            : contentSource.body);
                    const params = extractTemplateParams(expandedBody);
                    const blockingParams = getBlockingTemplateParams(
                        contentSource.inputSchema,
                        contentSource.params,
                        params,
                    );
                    const remainsTemplate = use.template === true || blockingParams.length > 0;
                    cDefs.set(use.id, {
                        ...contentSource,
                        id: use.id,
                        template: remainsTemplate,
                        params: params.length > 0 ? params : undefined,
                        body: expandedBody,
                        ...(use.outputMode ? { outputMode: use.outputMode } : {}),
                        ...(use.externalRef ? { externalRef: use.externalRef } : {}),
                        ...(cachedContentOutput ? { output: cachedContentOutput } : {}),
                    });
                } else if (existingContent) {
                    continue;
                } else if (materializedBody && useExec?.kind === "content") {
                    // Fallback: source content definition unavailable (e.g., import output
                    // is an external stub not yet hydrated), but engine has returned the
                    // materialized body via use execution hydration. Use it directly so
                    // ::render[id]:: can display the content block without waiting for a retry.
                    cDefs.set(use.id, {
                        id: use.id,
                        kind: "content",
                        template: false,
                        body: materializedBody,
                        range: use.range ?? { start: 0, end: 0 },
                        ...(use.outputMode ? { outputMode: use.outputMode } : {}),
                        ...(use.externalRef ? { externalRef: use.externalRef } : {}),
                        ...(cachedContentOutput ? { output: cachedContentOutput } : {}),
                    });
                }
            }
        }
        return cDefs;
    }, [parsedDocument, definitions, engineContentDefinitions, useExecutions, contentExecutions, contentScopedUnits, forScopedUnits, materializedUses]);
    const contentDefinitions = useStableSemanticValue(rawContentDefinitions);

    const chromeValue = useMemo<PaneChromeContextValue>(
        () => ({
            paneId,
            tabId,
            filePath: tab?.filePath ?? "",
            sourceFilePath: tab?.sourceFilePath,
            title: tab?.title ?? "Untitled",
            kind: tab?.kind,
            sessionId,
            splitPercent,
            previewWidth,
            isFocused,
        }),
        [
            paneId,
            tabId,
            tab?.filePath,
            tab?.sourceFilePath,
            tab?.title,
            tab?.kind,
            sessionId,
            splitPercent,
            previewWidth,
            isFocused,
        ],
    );

    const editorValue = useMemo<PaneEditorContextValue>(
        () => ({
            paneId,
            tabId,
            tab,
            parsedDocument: parsedDocument ?? null,
            definitions,
            definitionOutputs,
            documentDefinitionOutputs,
            forDefinitions,
            contentDefinitions,
            executions,
            forExecutions,
            contentExecutions,
            importExecutions,
            useExecutions,
            uses: materializedUses,
            sessionId,
            viewMode,
            isFocused,
        }),
        [
            paneId,
            tabId,
            tab,
            parsedDocument,
            definitions,
            definitionOutputs,
            documentDefinitionOutputs,
            forDefinitions,
            contentDefinitions,
            executions,
            forExecutions,
            contentExecutions,
            importExecutions,
            useExecutions,
            materializedUses,
            sessionId,
            viewMode,
            isFocused,
        ],
    );

    return (
        <PaneChromeContext.Provider value={chromeValue}>
            <PaneEditorContext.Provider value={editorValue}>
                {children}
            </PaneEditorContext.Provider>
        </PaneChromeContext.Provider>
    );
}

export function PaneRenderProvider({ children }: { children: React.ReactNode }) {
    const editor = usePaneEditorContext();
    const chrome = usePaneChromeContext();
    const activeTabId = useEditorStore((s) => s.activeTabId);
    const engineForBlocks = usePluginExecutionStore((s) => {
        if (editor.tabId === activeTabId) return s.engineForBlocks;
        const cachedSessionId = s.tabSessionMap.get(editor.tabId);
        return cachedSessionId
            ? (s.sessionExecutionCache.get(cachedSessionId)?.engineForBlocks ?? EMPTY_FOR_BLOCKS)
            : EMPTY_FOR_BLOCKS;
    });
    const rawImportDocuments = useMemo(
        () => collectImportDocuments(editor.parsedDocument?.imports, editor.importExecutions),
        [editor.parsedDocument?.imports, editor.importExecutions],
    );
    const importDocuments = useStableSemanticValue(rawImportDocuments);

    const renderValue = useMemo<PaneRenderContextValue>(
        () => ({
            paneId: editor.paneId,
            tabId: editor.tabId,
            tab: editor.tab,
            parsedDocument: editor.parsedDocument,
            definitions: editor.definitions,
            documentDefinitionOutputs: editor.documentDefinitionOutputs,
            forDefinitions: editor.forDefinitions,
            engineForBlocks,
            contentDefinitions: editor.contentDefinitions,
            importDocuments,
            uses: editor.uses,
            sessionId: editor.sessionId,
            viewMode: editor.viewMode,
            splitPercent: chrome.splitPercent,
            previewWidth: chrome.previewWidth,
            isFocused: editor.isFocused,
        }),
        [
            editor.paneId,
            editor.tabId,
            editor.tab,
            editor.parsedDocument,
            editor.definitions,
            editor.documentDefinitionOutputs,
            editor.forDefinitions,
            engineForBlocks,
            editor.contentDefinitions,
            importDocuments,
            editor.uses,
            editor.sessionId,
            editor.viewMode,
            chrome.splitPercent,
            chrome.previewWidth,
            editor.isFocused,
        ],
    );

    const value = useMemo<PaneContextValue>(
        () => ({
            paneId: editor.paneId,
            tabId: editor.tabId,
            tab: editor.tab,
            parsedDocument: editor.parsedDocument,
            definitions: editor.definitions,
            definitionOutputs: editor.definitionOutputs,
            documentDefinitionOutputs: editor.documentDefinitionOutputs,
            forDefinitions: editor.forDefinitions,
            engineForBlocks,
            contentDefinitions: editor.contentDefinitions,
            executions: editor.executions,
            forExecutions: editor.forExecutions,
            contentExecutions: editor.contentExecutions,
            importExecutions: editor.importExecutions,
            importDocuments,
            useExecutions: editor.useExecutions,
            uses: editor.uses,
            sessionId: editor.sessionId,
            viewMode: editor.viewMode,
            splitPercent: chrome.splitPercent,
            previewWidth: chrome.previewWidth,
            isFocused: editor.isFocused,
        }),
        [
            editor.paneId,
            editor.tabId,
            editor.tab,
            editor.parsedDocument,
            editor.definitions,
            editor.definitionOutputs,
            editor.documentDefinitionOutputs,
            editor.forDefinitions,
            engineForBlocks,
            editor.contentDefinitions,
            editor.executions,
            editor.forExecutions,
            editor.contentExecutions,
            editor.importExecutions,
            importDocuments,
            editor.useExecutions,
            editor.uses,
            editor.sessionId,
            editor.viewMode,
            chrome.splitPercent,
            chrome.previewWidth,
            editor.isFocused,
        ],
    );

    return (
        <PaneRenderContext.Provider value={renderValue}>
            <PaneContext.Provider value={value}>{children}</PaneContext.Provider>
        </PaneRenderContext.Provider>
    );
}

/* ── Hook ── */

/**
 * Access the current pane's context data.
 * Must be used within a PaneProvider.
 */
export function usePaneContext(): PaneContextValue {
    const ctx = useContext(PaneContext);
    if (!ctx) {
        throw new Error(
            "usePaneContext() must be used within a <PaneRenderProvider>. " +
            "Wrap render-heavy consumers in <PaneRenderProvider> inside <PaneProvider>.",
        );
    }
    return ctx;
}

export function usePaneRenderContext(): PaneRenderContextValue {
    const ctx = useContext(PaneRenderContext);
    if (!ctx) {
        throw new Error(
            "usePaneRenderContext() must be used within a <PaneRenderProvider>. " +
            "Wrap render-heavy consumers in <PaneRenderProvider> inside <PaneProvider>.",
        );
    }
    return ctx;
}

export function usePaneRenderContextOptional(): PaneRenderContextValue | null {
    return useContext(PaneRenderContext);
}

export function usePaneEditorContext(): PaneEditorContextValue {
    const ctx = useContext(PaneEditorContext);
    if (!ctx) {
        throw new Error(
            "usePaneEditorContext() must be used within a <PaneProvider>. " +
            "Wrap your component tree in <PaneProvider paneId={...}>.",
        );
    }
    return ctx;
}

export function usePaneEditorContextOptional(): PaneEditorContextValue | null {
    return useContext(PaneEditorContext);
}

export function usePaneChromeContext(): PaneChromeContextValue {
    const ctx = useContext(PaneChromeContext);
    if (!ctx) {
        throw new Error(
            "usePaneChromeContext() must be used within a <PaneProvider>. " +
            "Wrap your component tree in <PaneProvider paneId={...}>.",
        );
    }
    return ctx;
}

export function usePaneChromeContextOptional(): PaneChromeContextValue | null {
    return useContext(PaneChromeContext);
}

/**
 * Access the pane context if available, or return null.
 * Useful for components that may or may not be within a PaneProvider.
 */
export function usePaneContextOptional(): PaneContextValue | null {
    return useContext(PaneContext);
}
